import { useState } from "react";
import type { KitchenList, MealFavorite, MealSlotsPreset, WeekStart } from "../../../shared/kitchen/models";
import { useHousehold } from "../../app/useHousehold";
import { Icon } from "../../ui/Icon";
import { OutsideLink } from "../../ui/OutsideLink";
import { Confirm } from "../../ui/Sheet";
import { useFavoriteActions, useKitchenSettings, useListAdmin, useLists, useMealPicks, useSaveKitchenSettings } from "../../widgets/kitchen/api";
import "../../widgets/kitchen/kitchen.css";

/**
 * Admin → Kitchen: meal-plan defaults, the household's favorite meals, and
 * shopping/household lists. Structure changes need an adult account (the
 * server enforces it too); everyone else sees a read-only view.
 */
export function KitchenSection() {
  const { isAdmin } = useHousehold();
  return (
    <div className="form ks">
      <h3>Kitchen &amp; meals</h3>
      <p className="muted">Defaults for the meal planner, the favorites offered when planning a meal, and the lists on the Shopping widget.</p>
      {!isAdmin && <p className="banner">Only adults can change these. You can still plan meals, tick off lists and run timers from the widgets.</p>}
      <MealDefaults readOnly={!isAdmin} />
      <Favorites readOnly={!isAdmin} />
      <Lists readOnly={!isAdmin} />
    </div>
  );
}

function MealDefaults({ readOnly }: { readOnly: boolean }) {
  const settings = useKitchenSettings();
  const save = useSaveKitchenSettings();
  const [error, setError] = useState<string | null>(null);
  const s = settings.data;
  const set = (patch: { mealSlots?: MealSlotsPreset; weekStart?: WeekStart }) => {
    setError(null);
    save.mutate(patch, { onError: (e) => setError(e.message) });
  };
  return (
    <section className="ks-block">
      <h4>Meal planner</h4>
      {!s ? (
        <p className="muted small">{settings.isError ? `Couldn’t load: ${settings.error.message}` : "Loading…"}</p>
      ) : (
        <>
          <div className="field">
            <span>Meals to plan</span>
            <div className="segmented">
              {(
                [
                  ["dinner", "Dinner only"],
                  ["all", "Breakfast, lunch & dinner"],
                ] as const
              ).map(([v, label]) => (
                <button key={v} className={s.mealSlots === v ? "on" : ""} onClick={() => set({ mealSlots: v })} disabled={readOnly || save.isPending} aria-pressed={s.mealSlots === v}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="field">
            <span>Week starts on</span>
            <div className="segmented">
              {(
                [
                  ["monday", "Monday"],
                  ["sunday", "Sunday"],
                ] as const
              ).map(([v, label]) => (
                <button key={v} className={s.weekStart === v ? "on" : ""} onClick={() => set({ weekStart: v })} disabled={readOnly || save.isPending} aria-pressed={s.weekStart === v}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <p className="muted small">Each Meal planner widget can override these in its own settings.</p>
        </>
      )}
      {error && <p className="error-text">{error}</p>}
    </section>
  );
}

function Favorites({ readOnly }: { readOnly: boolean }) {
  const { favorites } = useMealPicks(true);
  const actions = useFavoriteActions();
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [editing, setEditing] = useState<{ id: string; title: string; url: string } | null>(null);
  const [deleting, setDeleting] = useState<MealFavorite | null>(null);
  const [error, setError] = useState<string | null>(null);
  const list = favorites.data ?? [];

  const add = () => {
    if (!title.trim()) return;
    setError(null);
    actions.create.mutate(
      { title: title.trim(), recipeUrl: url.trim() || null },
      {
        onSuccess: () => {
          setTitle("");
          setUrl("");
        },
        onError: (e) => setError(e.message),
      },
    );
  };
  const saveEdit = () => {
    if (!editing || !editing.title.trim()) return;
    setError(null);
    actions.update.mutate(
      { id: editing.id, patch: { title: editing.title.trim(), recipeUrl: editing.url.trim() || null } },
      { onSuccess: () => setEditing(null), onError: (e) => setError(e.message) },
    );
  };

  return (
    <section className="ks-block">
      <h4>Favorite meals</h4>
      <p className="muted small">Offered as one-tap picks when anyone plans a meal.</p>
      {favorites.isPending ? (
        <p className="muted small">Loading…</p>
      ) : list.length === 0 ? (
        <p className="muted small">No favorites yet.</p>
      ) : (
        <table className="table ks-table">
          <thead>
            <tr>
              <th>Meal</th>
              <th>Recipe link</th>
              {!readOnly && <th aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {list.map((f) =>
              editing?.id === f.id ? (
                <tr key={f.id}>
                  <td>
                    <input className="input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} onKeyDown={(e) => e.key === "Enter" && saveEdit()} maxLength={120} aria-label="Meal name" autoFocus />
                  </td>
                  <td>
                    <input className="input" value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} onKeyDown={(e) => e.key === "Enter" && saveEdit()} maxLength={500} inputMode="url" placeholder="https://…" aria-label="Recipe link" />
                  </td>
                  <td className="ks-actions">
                    <button className="btn btn-sm btn-primary" onClick={saveEdit} disabled={actions.update.isPending || !editing.title.trim()}>
                      Save
                    </button>
                    <button className="btn btn-sm" onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </td>
                </tr>
              ) : (
                <tr key={f.id}>
                  <td className="ks-strong">{f.title}</td>
                  <td className="ks-url">
                    {f.recipeUrl ? (
                      <OutsideLink href={f.recipeUrl} title={`Recipe: ${f.title}`}>
                        {f.recipeUrl.replace(/^https?:\/\/(www\.)?/, "")}
                      </OutsideLink>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  {!readOnly && (
                    <td className="ks-actions">
                      <button className="btn btn-sm" onClick={() => setEditing({ id: f.id, title: f.title, url: f.recipeUrl ?? "" })}>
                        <Icon name="edit" size={16} /> Rename
                      </button>
                      <button className="btn btn-sm btn-danger-ghost" onClick={() => setDeleting(f)} aria-label={`Delete ${f.title}`}>
                        <Icon name="trash" size={16} />
                      </button>
                    </td>
                  )}
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}
      {!readOnly && (
        <form
          className="row ks-add"
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
        >
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a favorite, e.g. Taco Tuesday" maxLength={120} aria-label="New favorite meal" />
          <input className="input" value={url} onChange={(e) => setUrl(e.target.value)} inputMode="url" placeholder="Recipe link (optional)" maxLength={500} aria-label="Recipe link (optional)" />
          <button className="btn" type="submit" disabled={!title.trim() || actions.create.isPending}>
            <Icon name="plus" size={18} /> Add
          </button>
        </form>
      )}
      {error && <p className="error-text">{error}</p>}
      {deleting && (
        <Confirm
          title="Delete favorite?"
          message={`“${deleting.title}” will no longer be offered as a quick pick. Meals already planned stay as they are.`}
          confirmLabel="Delete"
          danger
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            const f = deleting;
            setDeleting(null);
            actions.remove.mutate(f.id, { onError: (e) => setError(e.message) });
          }}
        />
      )}
    </section>
  );
}

function Lists({ readOnly }: { readOnly: boolean }) {
  const q = useLists();
  const actions = useListAdmin();
  const [name, setName] = useState("");
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState<KitchenList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lists = q.data?.lists ?? [];
  const counts = (id: string) => {
    const items = (q.data?.items ?? []).filter((i) => i.listId === id);
    return { open: items.filter((i) => !i.checkedAt).length, total: items.length };
  };

  const create = () => {
    if (!name.trim()) return;
    setError(null);
    actions.create.mutate(name.trim(), { onSuccess: () => setName(""), onError: (e) => setError(e.message) });
  };
  const saveRename = () => {
    if (!renaming?.name.trim()) return;
    setError(null);
    actions.rename.mutate({ id: renaming.id, name: renaming.name.trim() }, { onSuccess: () => setRenaming(null), onError: (e) => setError(e.message) });
  };

  return (
    <section className="ks-block">
      <h4>Lists</h4>
      <p className="muted small">Shopping and household lists. A Shopping widget shows every list as tabs, or one list by name.</p>
      {q.isPending ? (
        <p className="muted small">Loading…</p>
      ) : lists.length === 0 ? (
        <p className="muted small">No lists.</p>
      ) : (
        <table className="table ks-table">
          <thead>
            <tr>
              <th>List</th>
              <th>Items</th>
              {!readOnly && <th aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {lists.map((l) => {
              const c = counts(l.id);
              return (
                <tr key={l.id}>
                  <td className="ks-strong">
                    {renaming?.id === l.id ? (
                      <input className="input" value={renaming.name} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && saveRename()} maxLength={40} aria-label="List name" autoFocus />
                    ) : (
                      l.name
                    )}
                  </td>
                  <td className="muted">
                    {c.open} to get{c.total > c.open ? ` · ${c.total - c.open} checked` : ""}
                  </td>
                  {!readOnly && (
                    <td className="ks-actions">
                      {renaming?.id === l.id ? (
                        <>
                          <button className="btn btn-sm btn-primary" onClick={saveRename} disabled={actions.rename.isPending || !renaming.name.trim()}>
                            Save
                          </button>
                          <button className="btn btn-sm" onClick={() => setRenaming(null)}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="btn btn-sm" onClick={() => setRenaming({ id: l.id, name: l.name })}>
                            <Icon name="edit" size={16} /> Rename
                          </button>
                          <button className="btn btn-sm btn-danger-ghost" onClick={() => setDeleting(l)} aria-label={`Delete ${l.name}`}>
                            <Icon name="trash" size={16} />
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {!readOnly && (
        <form
          className="row ks-add"
          onSubmit={(e) => {
            e.preventDefault();
            create();
          }}
        >
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="New list, e.g. Hardware store" maxLength={40} aria-label="New list name" />
          <button className="btn" type="submit" disabled={!name.trim() || actions.create.isPending}>
            <Icon name="plus" size={18} /> Create list
          </button>
        </form>
      )}
      {error && <p className="error-text">{error}</p>}
      {deleting && (
        <Confirm
          title={`Delete “${deleting.name}”?`}
          message={`The list and its ${counts(deleting.id).total} item${counts(deleting.id).total === 1 ? "" : "s"} will be removed for everyone. Widgets pinned to this list will show “No list called ${deleting.name}”.`}
          confirmLabel="Delete list"
          danger
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            const l = deleting;
            setDeleting(null);
            actions.remove.mutate(l.id, { onError: (e) => setError(e.message) });
          }}
        />
      )}
    </section>
  );
}
